/*
 * KBPendingPanel
 * ────────────────
 * Per-portfolio review queue for KB entries the AI asset manager has
 * proposed. Renders a single Chip ("N learned") that opens a Drawer
 * listing pending entries with approve / edit-then-approve / reject
 * controls.
 *
 * Backend contract:
 *   GET    /api/portfolios/:id/kb/pending                  → { pending: [...] }
 *   POST   /api/portfolios/:id/kb/pending/:entryId/approve { edited_content? }
 *   POST   /api/portfolios/:id/kb/pending/:entryId/reject  { reason? }
 *
 * Refresh strategy:
 *   • Loads on mount + whenever `refreshKey` changes (parent bumps it
 *     after each agent turn, so newly-proposed entries appear without
 *     the user reopening the drawer).
 *   • Reports the pending count up to the parent via `onCountChange`
 *     so the chip can also live in the chat header.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box, Chip, Drawer, IconButton, TextField, Typography, Tooltip,
  CircularProgress, Divider, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';


export default function KBPendingPanel({ portfolioId, refreshKey = 0, onCountChange }) {
  const [pending, setPending]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [open, setOpen]         = useState(false);

  const loadPending = useCallback(async () => {
    if (!portfolioId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/kb/pending`,
        { timeout: 15_000 },
      );
      const items = res.data?.pending || [];
      setPending(items);
      onCountChange?.(items.length);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'failed to load');
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, onCountChange]);

  useEffect(() => { loadPending(); }, [loadPending, refreshKey]);

  if (!pending.length && !error) {
    // Nothing to show — render nothing rather than an empty pill.
    return null;
  }

  return (
    <>
      <Tooltip
        arrow
        title={
          pending.length
            ? `${pending.length} KB ${pending.length === 1 ? 'entry' : 'entries'} awaiting your review`
            : 'No pending KB entries'
        }
      >
        <Chip
          icon={<AutoAwesomeIcon sx={{ fontSize: 16, mr: -0.5 }} />}
          label={
            <Box component="span" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
              {pending.length} learned
            </Box>
          }
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            // amber tinted chip — matches ToolPill styling in PortfolioAgent.js
            bgcolor: 'rgba(217, 138, 43, 0.10)',
            color: 'primary.main',
            border: '1px solid',
            borderColor: 'rgba(217, 138, 43, 0.25)',
            height: 26,
            cursor: 'pointer',
            '& .MuiChip-label': { px: 0.75 },
            '&:hover': { bgcolor: 'rgba(217, 138, 43, 0.18)' },
          }}
        />
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}
      >
        <PanelHeader
          count={pending.length}
          onClose={() => setOpen(false)}
          onRefresh={loadPending}
          loading={loading}
        />
        {error && (
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(239,68,68,0.08)', color: '#dc2626', fontSize: '0.85rem' }}>
            {error}
          </Box>
        )}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {pending.length === 0 && !loading && (
            <Box sx={{ p: 3, color: 'text.secondary', fontSize: '0.88rem' }}>
              No pending entries. The agent will propose new ones as it
              learns durable facts about this portfolio.
            </Box>
          )}
          {pending.map((entry) => (
            <PendingEntry
              key={entry.id}
              entry={entry}
              portfolioId={portfolioId}
              onResolved={loadPending}
            />
          ))}
        </Box>
      </Drawer>
    </>
  );
}


function PanelHeader({ count, onClose, onRefresh, loading }) {
  return (
    <Box sx={{
      px: 2.5, py: 1.75,
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex', alignItems: 'center', gap: 1.5,
    }}>
      <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
          Agent learned
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          Review entries before they become part of this portfolio's knowledge base
        </Typography>
      </Box>
      {loading && <CircularProgress size={14} sx={{ color: 'primary.main' }} />}
      <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
    </Box>
  );
}


function PendingEntry({ entry, portfolioId, onResolved }) {
  const [editing, setEditing] = useState(false);
  const [edited, setEdited]   = useState(entry.content || '');
  const [reason, setReason]   = useState('');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);
  const [askingReject, setAskingReject] = useState(false);

  const approve = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/kb/pending/${entry.id}/approve`,
        editing ? { edited_content: edited } : {},
        { timeout: 15_000 },
      );
      onResolved?.();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'approve failed');
    } finally { setBusy(false); }
  }, [portfolioId, entry.id, editing, edited, onResolved]);

  const reject = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      await axios.post(
        `${API_BASE_URL}/api/portfolios/${portfolioId}/kb/pending/${entry.id}/reject`,
        reason.trim() ? { reason: reason.trim() } : {},
        { timeout: 15_000 },
      );
      onResolved?.();
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'reject failed');
    } finally { setBusy(false); }
  }, [portfolioId, entry.id, reason, onResolved]);

  return (
    <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Typography sx={{
          fontFamily: 'ui-monospace, monospace',
          fontSize: '0.78rem',
          color: 'primary.main',
          bgcolor: 'rgba(217,138,43,0.08)',
          px: 0.75, py: 0.25, borderRadius: 0.75,
        }}>
          {entry.file_path}
        </Typography>
        <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>
          {formatRelative(entry.created_at)}
        </Typography>
      </Box>

      {entry.agent_reasoning && (
        <Typography sx={{
          fontSize: '0.78rem',
          color: 'text.secondary',
          fontStyle: 'italic',
          mb: 1,
        }}>
          "{entry.agent_reasoning}"
        </Typography>
      )}

      {editing ? (
        <TextField
          value={edited}
          onChange={(e) => setEdited(e.target.value)}
          multiline
          minRows={3}
          maxRows={12}
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            '& .MuiInputBase-input': {
              fontSize: '0.88rem',
              fontFamily: 'ui-monospace, monospace',
              lineHeight: 1.5,
            },
          }}
        />
      ) : (
        <Typography sx={{
          fontSize: '0.88rem',
          whiteSpace: 'pre-wrap',
          lineHeight: 1.55,
          color: 'text.primary',
          bgcolor: 'rgba(0,0,0,0.02)',
          p: 1.25,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}>
          {entry.content}
        </Typography>
      )}

      {askingReject && (
        <TextField
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this not worth saving? (optional — the agent uses this to adjust)"
          fullWidth
          size="small"
          sx={{ mt: 1 }}
        />
      )}

      {err && (
        <Typography sx={{ fontSize: '0.78rem', color: '#dc2626', mt: 1 }}>
          {err}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.25, justifyContent: 'flex-end' }}>
        {!editing && !askingReject && (
          <>
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => setEditing(true)}
              disabled={busy}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Edit
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={() => setAskingReject(true)}
              disabled={busy}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Reject
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckIcon fontSize="small" />}
              onClick={approve}
              disabled={busy}
              sx={{ textTransform: 'none', fontSize: '0.8rem', boxShadow: 'none' }}
            >
              Approve
            </Button>
          </>
        )}
        {editing && (
          <>
            <Button
              size="small"
              onClick={() => { setEditing(false); setEdited(entry.content || ''); }}
              disabled={busy}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckIcon fontSize="small" />}
              onClick={approve}
              disabled={busy || !edited.trim()}
              sx={{ textTransform: 'none', fontSize: '0.8rem', boxShadow: 'none' }}
            >
              Approve edit
            </Button>
          </>
        )}
        {askingReject && (
          <>
            <Button
              size="small"
              onClick={() => { setAskingReject(false); setReason(''); }}
              disabled={busy}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={reject}
              disabled={busy}
              sx={{ textTransform: 'none', fontSize: '0.8rem', boxShadow: 'none' }}
            >
              Confirm reject
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}


function formatRelative(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60_000);
  if (min < 1)   return 'just now';
  if (min < 60)  return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24)   return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7)     return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
